import { useCallback, useEffect, useMemo, useState } from 'react'
import { useQueryParams } from 'use-query-params'
import { type WarehouseLocation } from 'legacy/common/types'
import {
  makeGenericStringQueryParamEncoder,
  stringQueryParamEncoder,
} from 'utils/query-params/string.utils'
import { PartnerPickDeviceStatusFilterEnum } from 'domains/partner-pick-devices/types/partnerPickDevices.types'

interface Props {
  warehouseLocations: WarehouseLocation[]
}

export const usePartnerPickDevicesListQueryParams = ({ warehouseLocations }: Props) => {
  const [queryParams, setQueryParams] = useQueryParams({
    warehouseLocationId: stringQueryParamEncoder,
    status: makeGenericStringQueryParamEncoder<PartnerPickDeviceStatusFilterEnum>(),
  })
  const [page, setPage] = useState(1) // we need the page number to support infinite loading

  const warehouseLocationId = useMemo(() => {
    if (queryParams.warehouseLocationId) {
      return queryParams.warehouseLocationId
    }

    return warehouseLocations[0]?.id
  }, [queryParams.warehouseLocationId, warehouseLocations])

  const setWarehouseLocationId = useCallback(
    (warehouseLocationId: string) => {
      setQueryParams({
        warehouseLocationId,
      })
      setPage(1)
    },
    [setQueryParams]
  )

  const setStatus = useCallback(
    (status: PartnerPickDeviceStatusFilterEnum) => {
      setQueryParams({
        status,
      })
      setPage(1)
    },
    [setQueryParams]
  )

  useEffect(() => {
    // set default values for query params
    if (!queryParams.warehouseLocationId) {
      setWarehouseLocationId(warehouseLocations[0]?.id)
    }

    if (!queryParams.status) {
      setStatus(PartnerPickDeviceStatusFilterEnum.Active)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    queryParams: {
      warehouseLocationId,
      status: queryParams.status,
      page,
    },
    setWarehouseLocationId,
    setStatus,
    setPage,
  }
}
